import { type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Action, Text } from '@soluto-private/mx-asurion-ui-react';

import { useInViewAnalytics } from '@service-initiation/shared-data-access-analytics';
import { stringifyAddress } from '@service-initiation/shared-data-access-google';
import { CheckCircleIcon } from '@service-initiation/shared-ui-check-circle-icon';
import { HorizontalRule } from '@service-initiation/shared-ui-horizontal-rule';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { Spacer } from '@service-initiation/shared-ui-spacer';
import { formatPhoneNumberWithDashes } from '@service-initiation/shared-utils-helpers';
import { useAsyncAtom } from '@service-initiation/shared-utils-jotai';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  selectedAppointmentAsyncAtom,
  useCustomerDetailsValue,
  useRemoteTechValue,
} from '@service-initiation/tech-care-data-access-store';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';
import {
  formatDateWithoutYear,
  formatTime,
} from '@service-initiation/tech-care-utils-date-time';

import { Map } from './Map';
import { Subtitle } from './Subtitle';
import van from './assets/van.png';

const Container = styled.div``;

const Section = styled.section``;

const MapContainer = styled.div`
  position: relative;
`;

const Image = styled.img`
  position: absolute;
  height: 72px;
  bottom: -32px;
`;

const OrderedList = styled.ol`
  list-style: auto;
  list-style-position: outside;
  padding-inline-start: 32px;
`;

const UnorderedList = styled.ul`
  list-style: disc;
  list-style-position: outside;
  padding-inline-start: 16px;
`;

const ListItem = styled.li`
  &::marker {
    font-weight: 700;
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const TEXT = TranslationEntries.Repairs.Confirmation.RemoteTech;

export const RemoteTechConfirmationPage: FC = () => {
  const { t } = useTranslation();
  const remoteTech = useRemoteTechValue();
  const customer = useCustomerDetailsValue();
  const { value: appointment } = useAsyncAtom(selectedAppointmentAsyncAtom);
  const appointmentDate = appointment?.date;
  const appointmentEndTime = appointment?.endTime;
  const appointmentStartTime = appointment?.startTime;
  const { ref: elementRef } = useInViewAnalytics(
    'RemoteTechConfirmation',
    {
      ...(customer?.phoneNumber && {
        mdn: customer?.phoneNumber,
      }),
      CompletedFlow: true,
      marketingCommunicationsOptIn: customer?.agreeToEmailOffers || false,
    },
    {
      Email: customer?.email || '',
    }
  );

  return (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          <Container data-testid="confirmation-container" ref={elementRef}>
            <CheckCircleIcon />
            <StepTitle
              subtitle={t(TEXT.Subtitle)}
              title={
                <Trans
                  components={{
                    Pii: <span className="fs-mask" />,
                  }}
                  data-testid="confirmation-title"
                  i18nKey={TEXT.Title}
                  values={{ firstName: customer?.firstName }}
                />
              }
            />
            {remoteTech?.meetingAddress && (
              <>
                <MapContainer>
                  <Map address={stringifyAddress(remoteTech?.meetingAddress)} />
                  <Image alt="van" src={van} />
                </MapContainer>
                <Spacer size={64} />
              </>
            )}
            <Text as="p">
              <Trans
                components={{
                  b: <b />,
                }}
                data-testid="confirmation-title"
                i18nKey={TEXT.Paragraph}
                values={{
                  date: `${formatDateWithoutYear(
                    appointmentDate || ''
                  )} between ${formatTime(
                    appointmentStartTime || '',
                    appointmentEndTime || ''
                  )}`,
                }}
              />
            </Text>
            <Spacer size={32} />
            <Section>
              <Subtitle>{t(TEXT.WhatHappensNext.Title)}</Subtitle>
              <Spacer size={16} />
              <OrderedList>
                {[
                  TEXT.WhatHappensNext.Items.FirstItem,
                  TEXT.WhatHappensNext.Items.SecondItem,
                ].map((value) => (
                  <ListItem key={value}>
                    <Text>
                      <Trans
                        components={{
                          b: <b />,
                        }}
                        i18nKey={value}
                      />
                    </Text>
                  </ListItem>
                ))}
                {remoteTech?.storeInfo?.phone && (
                  <ListItem>
                    <Text>
                      {t(TEXT.WhatHappensNext.Items.ThirdItem)}
                      <Action
                        href={`tel:${remoteTech?.storeInfo?.phone}`}
                        variant="flat"
                      >
                        {formatPhoneNumberWithDashes(
                          remoteTech?.storeInfo?.phone
                        )}
                      </Action>
                    </Text>
                  </ListItem>
                )}
              </OrderedList>
            </Section>
            <Spacer size={32} />
            <HorizontalRule />
            <Spacer size={32} />
            <Section>
              <Subtitle>{t(TEXT.BeforeWeArrive.Title)}</Subtitle>
              <Spacer size={16} />
              <UnorderedList>
                {Object.entries(TEXT.BeforeWeArrive.Items).map(
                  ([key, value]) => (
                    <ListItem key={key}>
                      <Text>
                        <Trans
                          components={{
                            backupLink: (
                              <Action
                                href="https://www.asurion.com/connect/tech-tips/how-to-back-up-your-iphone-and-android-phone"
                                target="_blank"
                                variant="flat"
                              />
                            ),
                            fmsLink: (
                              <Action
                                href="https://www.asurion.com/connect/tech-tips/how-to-turn-off-find-my-iphone"
                                target="_blank"
                                variant="flat"
                              />
                            ),
                          }}
                          i18nKey={value}
                        />
                      </Text>
                    </ListItem>
                  )
                )}
              </UnorderedList>
            </Section>
          </Container>
        </LeftMainContentSection>
      </MainContentContainer>
    </PageContainer>
  );
};
